import React from 'react';
import styled, { css } from 'styled-components';
import { ComponentProps } from '../../../../index';
import { ROOT } from '../../../../../../constants/paths';
import useFireTrackingEvent from '../../../../../../hooks/useFireTrackingEvent';
import { iframeSizes } from '../../../../../../shared/constants/iframeSizes';
import { Appearance } from '../../../../../../shared/types';
import Icon from '../../../../../Icon';
import JustificationContainer from '../../../../../JustificationContainer';
import { Heading4 } from '../../../../../Typography';

import HighlightedHeader from './components/HighlightedHeader';
import CloseButton, {
  CLOSE_BUTTON_SIZE,
} from '../../../Large/Variants/Initial/components/CloseButton';
import useFrameSizeSelector from '../../../../../../hooks/useFrameSizeSelector';
import useWidgetContext from '../../../../../../hooks/useWidgetContext';
import NotificationBubble from '../../../NotificationBubble';

const Initial: React.FC<Omit<ComponentProps, 'size'>> = ({
  dataTestId,
  onDismiss,
  onTrigger,
  position,
  footer,
  header,
  headerLink,
  illustration,
  hasOnlyOneApp = false,
  ...rest
}) => {
  const currentBp = useFrameSizeSelector();
  const fireEvent = useFireTrackingEvent();
  const { apps } = useWidgetContext();
  const totalApps = Object.keys(apps).length;

  return (
    <Container
      {...rest}
      data-testid={dataTestId}
      $currentBp={currentBp}
      $size={Appearance.Small}
    >
      <HighlightedHeader
        header={header}
        illustration={illustration}
        onClick={() => {
          onTrigger(headerLink);
          fireEvent({
            event: 'ExpandWidget',
            path: headerLink,
            triggerType: 'headerClick',
          });
        }}
      />
      <TriggerFooter
        padding={['base']}
        align="start"
        dataTestId="trigger-footer"
        onClick={() => {
          const path = hasOnlyOneApp ? headerLink : ROOT;
          onTrigger(path);
          fireEvent({
            path,
            event: 'ExpandWidget',
            triggerType: 'footerClick',
          });
        }}
      >
        <StyledHeading4 $margin={[null]}>{footer}</StyledHeading4>
        <NotificationBubble>+{totalApps}</NotificationBubble>
      </TriggerFooter>
      <CloseButton
        $position={position}
        onClick={e => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <Icon name="close" />
      </CloseButton>
    </Container>
  );
};

const Container = styled.div<{
  $currentBp: 'desktop' | 'mobile';
  $size: Appearance;
}>(
  ({ theme, $currentBp, $size }) => css`
    position: relative;
    border-radius: ${theme.getSystem().border.radius.base};
    width: ${iframeSizes.collapsed[$size][$currentBp].width};

    box-shadow: ${theme.boxShadow('s')};
    cursor: pointer;

    margin-top: ${CLOSE_BUTTON_SIZE / 2}px;
    margin-left: ${CLOSE_BUTTON_SIZE / 2}px;
  `,
);

const StyledHeading4 = styled(Heading4)<{}>(
  ({ theme }) => css`
    margin-right: ${theme.space('xxxs')};
    font-size: ${theme.fontSize('xs')};
    text-decoration: underline;
  `,
);

const TriggerFooter = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    background: ${theme.color('primary', 'text')};
    color: ${theme.color('primary')};
    border-radius: 0 0 ${theme.getSystem().border.radius.base}
      ${theme.getSystem().border.radius.base};

    svg {
      height: 1em;
      width: 1em;
    }
  `,
);

export default Initial;
